import { httpGet, httpPost, httpDelete, httpPut } from "../http";
import { femaCapture } from "../../fema/capture";
import { documentsPageEndpoints } from "../endpoints";
import {
  ApiResponse,
  UploadDocumentResponse,
  ServiceResponse,
  UpdateDocumentRequest,
  SharedDocResponse,
} from "../types";

export class DocumentsPageService {
  static async uploadCustomerDocument(
    customerId: string | number,
    payload: FormData,
    isDocUploadedFromDecl: string,
    entrySource: string = ""
  ): Promise<UploadDocumentResponse> {
    try {
      const response = await httpPost<UploadDocumentResponse>(
        documentsPageEndpoints.uploadCustomerDocument(
          customerId,
          isDocUploadedFromDecl ?? "",
          entrySource ?? ""
        ),
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const result = response.data as any;
      return result?.data ?? result;
    } catch (error) {
      femaCapture("document_upload_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });

      throw error;
    }
  }

  static async getCustomerDocuments(
    customerId: string | number
  ): ServiceResponse<any[]> {
    try {
      const response = await httpGet<ApiResponse<UploadDocumentResponse[]>>(
        documentsPageEndpoints.getCustomerDocuments(customerId)
      );

      const result = response.data as any;
      return result?.data ?? result;
    } catch (error) {
      femaCapture("fetching_document_data_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });

      throw error;
    }
  }

  static async downloadCustomerDocument(
    fileId: string | number
  ): Promise<any> {
    try {
      // New backend returns JSON with SAS URL instead of blob data
      const response = await httpGet<any>(
        documentsPageEndpoints.downloadCustomerDocument(fileId),
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      const result = response.data as any;
      const payload = result?.data ?? result;

      // If backend returned a SAS URL, fetch the blob from it
      if (payload?.url && typeof payload.url === "string") {
        const blobResponse = await fetch(payload.url);
        return await blobResponse.blob();
      }

      // Fallback: response is already blob data (old backend)
      return response.data;
    } catch (error) {
      femaCapture("download_document_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });

      throw error;
    }
  }

  static async deleteCustomerDocument(
    customerId: string | number,
    id: string | number
  ): ServiceResponse<any[]> {
    try {
      const response = await httpDelete<ApiResponse>(
        documentsPageEndpoints.deleteCustomerDocument(customerId, id)
      );

      const result = response.data as any;
      return result?.data ?? result;
    } catch (error) {
      femaCapture("delete_document_data_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });

      throw error;
    }
  }

  static async updateCustomerDocument(
    payload: UpdateDocumentRequest
  ): Promise<SharedDocResponse> {
    try {
      const response = await httpPut<Document>(
        documentsPageEndpoints.updateCustomerDocument(),
        payload
      );

      const result = response.data as any;
      return result?.data ?? result;
    } catch (error) {
      femaCapture("update_document_data_error", error, {
        category: "api",
        severity: "error",
        skipSanitization: false,
        user: {},
      });

      throw error;
    }
  }
}
